import React, { Fragment } from 'react';

import { IComponent } from '../../../typings/generic-component';
import { Sport } from '../../../typings/motorsports';
import { RaceSessionStatus } from '../../../typings/race-session';
import { isNotEnded, isPreMatch } from '../../../utils/match-status';
import { getRaceSessionStatus, getSessionLabels } from '../../../utils/motorsports';
import { GPFLAG, placeholdersMap } from '../../../utils/placeholders-motorsports';
import { isFixture, isPlaying } from '../../../utils/race-status';
import { makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import { Cta, ImgPlaceholder, Text } from '../../atoms';
import { IMatchRow } from '../match-row/match-row';
import MatchStatusWrapper from '../match-status/match-status-wrapper';
import Team from '../team/team';

import './competition-row.scss';

export interface ICompetitionRow extends IComponent {
  link?: string | null;
  ariaLabel: string;
  status?: string;
}

export interface IFootballRow {
  match: IMatchRow;
}

export interface IMotorSportsRow {
  flagUrl?: string;
  nationality?: string;
  gpName: string;
  sessionName: string;
  sessionStatus: RaceSessionStatus;
  sessionDate?: string;
  sportTag?: Sport;
}

const classNamesFootball = () =>
  makeClassNames({
    teams: 'competition-row__teams',
    resultWrapper: 'competition-row__result-wrapper',
    result: 'competition-row__result',
    divider: 'competition-row__divider',
  });

const classNamesMotorSports = (sessionStatus: RaceSessionStatus) =>
  makeClassNames({
    gp: 'competition-row__gp',
    gpInfo: 'competition-row__gp-info',
    sessionStatus: [
      'competition-row__status',
      isFixture(sessionStatus) && 'competition-row__status--no-background',
      isPlaying(sessionStatus) && 'competition-row__status--red',
    ],
    gpName: 'competition-row__gp-name',
    session: 'competition-row__session',
  });

export default function CompetitionRow({ link, children, ariaLabel, trackingData, status }: ICompetitionRow) {
  const cn = makeClassNames({ competitionRow: 'competition-row' });

  return link ? (
    <Cta
      href={link}
      className={cn.competitionRow}
      aria-label={ariaLabel}
      trackingData={trackingData}
      data-status={status}
      uppercase={false}
    >
      {children}
    </Cta>
  ) : (
    <div className={cn.competitionRow} data-status={status}>
      {children}
    </div>
  );
}

CompetitionRow.Football = function CompetitionRowFootball({ match }: IFootballRow) {
  const cn = classNamesFootball();
  const { home, away, competitionPhase, status } = match;
  const { homepageUrl: hpUrlHome, goal: homeGoal } = home;
  const { homepageUrl: hpUrlAway, goal: awayGoal } = away;

  return (
    <Fragment>
      <div className={cn.teams}>
        <Team {...home} fontSize="16" logoSize="20" clickable={typeof hpUrlHome === 'string'} iconPosition="left" />
        <Team {...away} fontSize="16" logoSize="20" clickable={typeof hpUrlAway === 'string'} iconPosition="left" />
      </div>
      <div className={cn.resultWrapper}>
        {!isNotEnded(status) && !isPreMatch(status) && (
          <Fragment>
            <div className={cn.result} aria-label="Risultati">
              <span aria-label={`Goal ${home.name}`}>{homeGoal || '0'}</span>
              <span aria-label={`Goal ${away.name}`}>{awayGoal || '0'}</span>
            </div>
            <div className={cn.divider} />
          </Fragment>
        )}
        <MatchStatusWrapper matchPhase={competitionPhase ?? null} variant="Wrapped" backgroundColor {...match} />
      </div>
    </Fragment>
  );
};

CompetitionRow.MotorSports = function CompetitionRowMotorSports({
  flagUrl,
  nationality,
  sessionName,
  gpName,
  sessionStatus,
  sessionDate,
  sportTag,
}: IMotorSportsRow) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cn = classNamesMotorSports(sessionStatus);

  const sessionStatusLabel = getRaceSessionStatus(false);
  const sessionLabels = getSessionLabels(sportTag);

  return (
    <Fragment>
      <div className={cn.gp}>
        <ImgPlaceholder
          src={flagUrl}
          placeholder={placeholdersMap[GPFLAG]}
          aria-label={nationality}
          aria-hidden={flagUrl === '-'}
        />
        <div className={cn.gpInfo}>
          <Text fontSize="12" fontWeight="400" color="medium" className={cn.gpName}>
            {gpName}
          </Text>
          <Text fontSize="16" fontWeight="500" className={cn.session}>
            {sessionLabels[sessionName]}
          </Text>
        </div>
      </div>
      <div className={cn.sessionStatus}>
        {isFixture(sessionStatus)
          ? dateFormat({ date: sessionDate, format: 'HH:mm' })
          : sessionStatusLabel[sessionStatus]}
      </div>
    </Fragment>
  );
};
